import { UserManager, WebStorageStateStore } from "oidc-client-ts";
import { GITLAB_CLIENT_ID, GITLAB_BASE_URL } from "./constants";

const GITLAB_USER_KEY = "gitlab_user_data";

// Configure with appropriate settings for GitLab and refresh tokens
const oidcConfig = {
	authority: GITLAB_BASE_URL,
	client_id: GITLAB_CLIENT_ID,
	redirect_uri: `${window.location.origin}/callback`,
	scope: "read_api",
	response_type: "code",
	// Enable automatic token renewal
	automaticSilentRenew: true,
	// Needed for refresh tokens to work
	prompt: "consent",
	// Storage configuration
	stateStore: new WebStorageStateStore({ store: window.localStorage }),
	userStore: new WebStorageStateStore({ store: window.localStorage }),
	// GitLab endpoints
	metadata: {
		issuer: GITLAB_BASE_URL,
		authorization_endpoint: `${GITLAB_BASE_URL}/oauth/authorize`,
		token_endpoint: `${GITLAB_BASE_URL}/oauth/token`,
		userinfo_endpoint: `${GITLAB_BASE_URL}/api/v4/user`,
		end_session_endpoint: `${GITLAB_BASE_URL}/users/sign_out`,
	},
};

class GitLabUserManager extends UserManager {
	constructor(config) {
		super(config);
		this.setupEventHandlers();
	}

	setupEventHandlers() {
		// Handle token about to expire
		this.events.addAccessTokenExpiring(async () => {
			console.log("Token expiring, attempting renewal");
			try {
				await this.signinSilent();
			} catch (error) {
				console.error("Silent token renewal failed:", error);
			}
		});

		// Handle token already expired
		this.events.addAccessTokenExpired(async () => {
			if (document.location.pathname === "/callback") return;

			console.log("Token expired, attempting renewal with refresh token");
			try {
				await this.signinSilent();
			} catch (error) {
				console.error("Refresh token renewal failed:", error);
				// Redirect to login if renewal fails
				this.signinRedirect().catch((e) =>
					console.error("Redirect failed:", e),
				);
			}
		});

		// Handle user loaded event to enhance profile
		this.events.addUserLoaded(async (user) => {
			await this.enhanceUserProfile(user);
		});
	}

	async enhanceUserProfile(user) {
		if (!user || !user.access_token) return user;

		try {
			const userInfo = await this.fetchGitLabUserInfo(user.access_token);
			await this.storeEnhancedProfile(user, userInfo);
		} catch (error) {
			console.error("Error enhancing user profile:", error);
			// Use cached data if available
			const cachedData = localStorage.getItem(GITLAB_USER_KEY);
			if (cachedData) {
				user.profile = { ...user.profile, ...JSON.parse(cachedData) };
			}
		}
		return user;
	}

	async fetchGitLabUserInfo(accessToken) {
		const response = await fetch(`${GITLAB_BASE_URL}/api/v4/user`, {
			headers: { Authorization: `Bearer ${accessToken}` },
		});

		if (!response.ok) {
			throw new Error(`GitLab API error: ${response.status}`);
		}

		return response.json();
	}

	async storeEnhancedProfile(user, gitlabData) {
		// Combine existing and new data
		const existingData = localStorage.getItem(GITLAB_USER_KEY);
		const existingProfile = existingData ? JSON.parse(existingData) : {};

		const enhancedProfile = {
			...existingProfile,
			...gitlabData,
			// Keep track of token expiration
			token_expires_at: user.expires_at,
		};

		localStorage.setItem(GITLAB_USER_KEY, JSON.stringify(enhancedProfile));
		user.profile = { ...user.profile, ...enhancedProfile };
	}

	// Override to ensure enhanced profile is returned
	async signinRedirectCallback(url = window.location.href) {
		const user = await super.signinRedirectCallback(url);
		return this.enhanceUserProfile(user);
	}

	// Override to ensure enhanced profile is returned
	async signinSilent() {
		const user = await super.signinSilent();
		return this.enhanceUserProfile(user);
	}

	// Override to ensure enhanced profile is returned
	async getUser() {
		const user = await super.getUser();
		if (user) {
			const storedData = localStorage.getItem(GITLAB_USER_KEY);
			if (storedData) {
				user.profile = { ...user.profile, ...JSON.parse(storedData) };
			}
		}
		return user;
	}

	// Clean up stored data on logout
	async removeUser() {
		localStorage.removeItem(GITLAB_USER_KEY);
		return super.removeUser();
	}
}

export const userManager = new GitLabUserManager(oidcConfig);
